import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getConfigByApp,
  isUserAgentForBot,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {JOBFEED, WOMEN_AT_WORK} from '@constants';
import type {GetServerSidePropsContext} from 'next/types';
import loadSEO from '@helpers/loadSEO';
import {findJobsForHer} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useEffect, useState, useCallback, memo} from 'react';
import type {AppState} from '@jobseeker/common-core/types';
import {Box, Card, Grid, Skeleton} from '@mui/material';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {Image} from '@jobseeker/lotus/components/Image';
import NoJobsForHer from '@jobseeker/feature-job/components/NoJobsForHer';
import {useRouter} from 'next/router';
import {isCSR} from '@jobseeker/lotus/utils';
import {SeoIntrup} from '@layouts/SeoLayout';
import {useValidateAuth} from '@jobseeker/common-core';
import Dialog from '@jobseeker/lotus/components/Dialog';
import dynamic from 'next/dynamic';
import {Loader} from '@jobseeker/lotus/components';
import {jobfeedService} from '@jobseeker/common-core/services';
import {NoJobsFound} from '@jobseeker/feature-job/components';
import {
  handleTracking,
  openUrl,
  TrackEventUBA,
  InitiateUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {InjectSearchOverlay} from '@components';
import {experienceOptionsHirist} from '@jobseeker/lotus/components/JobSearchBar/data';
import {ROUTE_JOB_DETAIL} from '@jobseeker/feature-onboarding/constants';

type WowProps = {
  seoConfig: object;
  showSavedIcon: boolean;
  filters: {
    experience: string;
    loc: string[];
    category: string[];
  };
};

const DownloadAppJobdetail = dynamic(
  () => import('@jobseeker/lotus/components/DownloadAppJobdetailAndCatogary'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const JobFeedHarness = dynamic(
  () => import('@jobseeker/client-harness/components/JobHarness/JobfeedV2'),
  {
    loading: () => <Loader />,
  }
);

const LoginForm = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/AuthenticationHarness/AuthForm'
    ),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);
const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {
      ssr: false,
    }
  )
);
const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const WowPage = ({seoConfig}: WowProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, WOMEN_AT_WORK);
  const jobFeedStore = useSelector((state: AppState) => state.job);
  const [isAdsLoading, setIsAdsLoading] = useState<boolean>(true);
  const [ads, setAds] = useState([]);

  const [pageNo, setPageNo] = useState<number>(0);
  const [concat, setConcat] = useState<boolean>(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [formValue, setFormValue] = useState({
    experience: '',
    location: [],
    category: [],
  });
  const {searchOverlay: isSearchEnabled, device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const {isLoggedIn} = useSelector(
    (state: AppState) => state.auth,
    shallowEqual
  );

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const router = useRouter();

  useEffect(() => {
    const pageName = getUbaPageName();
    InitiateUBA(pageName);
    TrackEventUBA('eventPageView', 'view', {
      ref: (router.query.ref as string) || '',
      marketingEventDuration: "8th-11th Mar'2026",
      marketingEventName: 'WoW-WomenatWork',
      page: pageName,
      pageName: pageName,
      sessionUsername: `${sessionStorage.getItem('userId')}`,
    });
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(
      router.query as Record<string, string>
    );
    if (!queryParams.has('ref')) {
      queryParams.set('ref', 'wow');
      router.replace({query: queryParams.toString()}, undefined, {
        shallow: true,
      });
    }
  }, [router]);

  useEffect(() => {
    if (ads.length > 0) {
      setIsAdsLoading(false);
    }
  }, [ads]);

  useLoadAds(metaData.ads.wow.slot, setAds, {}, false);

  useValidateAuth(dispatch, false);
  const {enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  const routeToJobDetail = (jobSlug: string, jobId: string | number) => {
    openUrl(`${ROUTE_JOB_DETAIL}/${jobSlug}-${jobId}`);
  };

  const getExperienceRange = useCallback((value: number) => {
    const selected = experienceOptionsHirist.find(
      option => option.value === value
    );

    if (selected) {
      return {minExp: selected.minExp, maxExp: selected.maxExp};
    }

    return {minExp: 0, maxExp: 100};
  }, []);

  const JOBS_PER_PAGE = 50;

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        const start = pageNo * JOBS_PER_PAGE;
        const end = start + JOBS_PER_PAGE;

        await dispatch(
          findJobsForHer({
            concat: concat,
            femaleFilter: 1,
            start,
            end,
            minExp: formValue.experience
              ? getExperienceRange(Number(formValue.experience)).minExp
              : 0,
            maxExp: formValue.experience
              ? getExperienceRange(Number(formValue.experience)).maxExp
              : 50,
            categoryIds: formValue.category?.length ? formValue.category : '',
            joblocationIds: formValue.location?.length
              ? formValue.location
              : '',
          })
        );
      } catch (error) {
        console.log('Error fetching jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, [pageNo, formValue, dispatch, concat, getExperienceRange]);

  const sendEvent = (
    eventCatogary: string,
    eventName: string,
    action: string
  ) => {
    handleTracking(`${eventName}_${action}`, {
      event_category: `${eventCatogary}`,
      Origin: WOMEN_AT_WORK,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };

  const handleFilter = useCallback(
    (filterData: any, clear = false) => {
      const {experience, loc, category} = filterData;
      setPageNo(0);
      setConcat(false);

      setFormValue({
        experience: experience || '',
        location: loc || [],
        category: category || [],
      });

      const queryParams = new URLSearchParams(
        router.query as Record<string, string>
      );

      if (clear) {
        queryParams.delete('experience');
        queryParams.delete('loc');
        queryParams.delete('category');
      } else {
        // if (experience) queryParams.set('experience', experience);
        // else queryParams.delete('experience');
        // if (loc?.length) queryParams.set('loc', loc.join(','));
        // else queryParams.delete('loc');
        // if (category?.length) queryParams.set('category', category.join(','));
        // else queryParams.delete('category');
      }

      queryParams.set('ref', 'wow');

      router.replace({query: queryParams.toString()}, undefined, {
        shallow: true,
      });
    },
    [router]
  );

  const handleClearFilter = () => {
    setFormValue({
      experience: '',
      location: [],
      category: [],
    });
    setPageNo(0);
    setConcat(false);

    dispatch({
      type: 'job/jobFeed/set_filters',
      payload: {
        filters: {
          experience: '',
          location: [],
          category: [],
        },
        options: {
          showSort: true,
          clearFilter: true,
        },
      },
    });

    dispatch({
      type: 'job/jobFeed/set_filter_chips',
      payload: {chips: []},
    });

    sessionStorage.removeItem('filters');

    const queryParams = new URLSearchParams(
      router.query as Record<string, string>
    );
    queryParams.delete('experience');
    queryParams.delete('loc');
    queryParams.delete('category');
    queryParams.set('ref', 'wow');

    router.replace({query: queryParams.toString()}, undefined, {
      shallow: true,
    });
  };

  const onJobSave = () => {
    if (!isLoggedIn) {
      isAppIimjobs
        ? setLoginOpen(true)
        : dispatch({
            type: 'AppContext/set_login_popup',
            payload: true,
          });
    }
  };

  const handleFilterChange = useCallback((e: any, value?: any) => {
    const name = e?.target?.name;
    const newValue = value || e?.target?.value;

    if (!name && typeof e === 'string') {
      setFormValue(prev => ({
        ...prev,
        [e]: value,
      }));
    } else if (name) {
      setFormValue(prev => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      console.error(
        'Invalid arguments passed to handleFilterChange:',
        e,
        value
      );
    }
  }, []);

  const onLoadMore = () => {
    setPageNo(pageNo + 1);
    setConcat(true);
  };
  const onLoginSuccess = () => {
    if (isCSR()) {
      window.location.reload();
    }
  };

  const isFilterApplied =
    formValue.experience ||
    formValue.location.length ||
    formValue.category.length;

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={[]}
          variant={VersionVariants.VERSION_TWO}
        >
          {isCSR() && (
            <SeoIntrup pageNo={0} nextPage={false} type={WOMEN_AT_WORK} />
          )}
          <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />

          <Box
            display={isAppIimjobs ? 'flex' : undefined}
            flexDirection={isAppIimjobs ? 'column' : undefined}
            alignItems={isAppIimjobs ? 'center' : undefined}
            sx={{
              ...(isAppIimjobs
                ? {
                    mt: '-15px',
                    mx: {xs: '-16px', md: '0px'},
                  }
                : {
                    width: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                  }),
            }}
          >
            {/**here to before jobfeed store is code for banner */}
            <Box
              width="100%"
              sx={{
                aspectRatio: !isAppIimjobs
                  ? {xxs: '540/76', xs: '544/120', sm: '1440/293'}
                  : {xxs: '540/74', xs: '544/118', sm: '1440/291'},
                overflow: 'hidden',
              }}
            >
              {isAdsLoading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : (
                <Box sx={{width: '100%', height: '100%'}}>
                  <Image
                    src={ads[0]?.imageUrl || ads[0]?.['Image Url']}
                    alt={ads[0]?.title}
                    placeholder="blur"
                    blurDataURL={ads[0]?.imageUrl || ads[0]?.['Image Url']}
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                      marginBottom: isAppIimjobs ? '0px' : '-6px',
                    }}
                  />
                </Box>
              )}
            </Box>

            {jobFeedStore.jobfeed.length > 0 || isLoading || !isAppIimjobs ? (
              <Grid
                container
                spacing={2}
                sx={{
                  mt: isAppIimjobs ? {xxs: '-8px', md: 0} : {xxs: 1, md: 9},
                  width: '100%',
                }}
              >
                {' '}
                {isAppIimjobs && (
                  <Box
                    sx={{
                      width: true ? '100%' : {xxs: '100%', md: '134%'},
                      ml: {xxs: '8px', md: isAppIimjobs ? '6px' : 0},
                    }}
                  >
                    <AdsPanelHarness
                      variant={VersionVariants.VERSION_TWO}
                      showFeatured={false}
                      showLearnGrow={false}
                      showFeaturedInstitute={false}
                      showRolesAtLeadingBrands={false}
                      showPlatinumPartners={false}
                      showWowPlatinumPartners={true}
                      showWowFeaturedEmployer={false}
                      showWowGoldPartners={device === 'mobile'}
                      showGoldPartners={false}
                      showSmallBadges={false}
                      showLearningCenterCTA={false}
                      page={WOMEN_AT_WORK}
                    />
                  </Box>
                )}
                <Grid item xs={12} md={9}>
                  <Box
                    mb={{xxs: 2, md: 0}}
                    width="100%"
                    sx={{
                      mt: isAppIimjobs
                        ? {xxs: '-12px', md: '-10px'}
                        : {xxs: '-24px', md: '-10px'},
                      ml: isAppIimjobs ? {md: '-15px'} : 0,
                    }}
                  >
                    <JobFeedHarness
                      onLoadMore={onLoadMore}
                      routeToJobDetail={routeToJobDetail}
                      title={'Jobs'}
                      feedOnly={false}
                      onJobSave={onJobSave}
                      infiniteScroll={true}
                      onFilter={handleFilter}
                      onFilterChange={handleFilterChange}
                      onClearFilter={handleClearFilter}
                      variant={VersionVariants.VERSION_TWO}
                    />
                  </Box>
                </Grid>
                {/* Side section with DownloadAppJobdetail */}
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      mb: 3.5,
                      display: {xxs: 'none', md: 'block'},
                      mt: isAppIimjobs ? '-25px' : '200px',
                    }}
                  >
                    <>
                      <AdsPanelHarness
                        variant={VersionVariants.VERSION_TWO}
                        showFeatured={false}
                        showLearnGrow={false}
                        showFeaturedInstitute={false}
                        showRolesAtLeadingBrands={false}
                        showPlatinumPartners={false}
                        showGoldPartners={false}
                        showSmallBadges={false}
                        showLearningCenterCTA={false}
                        showWowPlatinumPartners={false}
                        showWowGoldPartners={true}
                        showWowFeaturedEmployer={true}
                        page={WOMEN_AT_WORK}
                      />
                      <DownloadAppJobdetail sendEvent={sendEvent} />
                    </>
                  </Box>
                </Grid>
              </Grid>
            ) : !isFilterApplied ? (
              <Card
                sx={{
                  width: '100%',
                  boxShadow: 2,
                  borderRadius: 4,
                }}
              >
                <NoJobsForHer />
              </Card>
            ) : (
              <Card
                sx={{
                  width: '100%',
                  boxShadow: 2,
                  borderRadius: 4,
                }}
              >
                <NoJobsFound clearFilter={handleClearFilter} showBadge={true} />
              </Card>
            )}
          </Box>
          {isAppIimjobs ? (
            <Dialog
              title="Login to your account"
              subtitle="Welcome back! 👋"
              invertedTitle
              variant={VersionVariants.VERSION_TWO}
              open={loginOpen}
              onClose={() => setLoginOpen(false)}
            >
              <LoginForm
                onSuccess={onLoginSuccess}
                variant={VersionVariants.VERSION_TWO}
              />
            </Dialog>
          ) : (
            <HiristAuthentication
              variant={VersionVariants.VERSION_TWO}
              onSuccess={onLoginSuccess}
              openDialog={enableLoginPopup}
              setOpenDialog={() => {
                dispatch({
                  type: 'AppContext/set_login_popup',
                  payload: false,
                });
              }}
            />
          )}
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const experience = (context.query?.experience as string) || '';
    const loc = (context.query?.loc as string) || '';
    const category = (context.query?.category as string) || '';

    const ssrHeaders = {
      'user-agent': context.req?.headers?.['user-agent'],
      'x-forwarded-for': context.req?.headers?.['x-forwarded-for']
        ?.toString()
        .split(',')[0],
      referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
    };

    const metaData = getConfigByApp(metaInfo, JOBFEED, true, WOMEN_AT_WORK);
    const slugUrl = context?.resolvedUrl || '';

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: WOMEN_AT_WORK},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const filters = {
      experience,
      loc,
      category,
    };
    store.dispatch({type: 'job/jobFeed/set_initial_state'});

    const isBotUser = isUserAgentForBot(context);

    if (isBotUser) {
      /**
       * Fetch the initial state for jobs list and related ads to show
       */
      store.dispatch({
        type: 'AppContext/set_bot',
        payload: true,
      });

      try {
        const femaleJobs = await jobfeedService.getFemaleJobs(
          1,
          0,
          0,
          '',
          '',
          0,
          50,
          undefined,
          undefined,
          undefined,
          ssrHeaders
        );

        store.dispatch({
          type: 'job/jobFeed/set_jobfeed',
          payload: {
            femaleJobs: femaleJobs?.data?.jobs,
            hasMore: femaleJobs?.data?.hasMore,
            totalJobs: femaleJobs?.data?.count,
          },
        });
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:200 ~ store jobfeed wow jobs=> ~ error:',
          error
        );
        return {
          notFound: true,
        };
      }
    }

    const seoConfig = await loadSEO(
      WOMEN_AT_WORK,
      {
        title: '',
        description: '',
      },
      slugUrl,
      true
    );

    return {
      props: {
        seoConfig,
        metaData,
        filters,
      },
    };
  }
);

export default WowPage;
